import React, {useCallback} from "react";
import {useDispatch, useSelector} from "react-redux";

import {GeoMapProps} from "./types";
import {AppDispatch, RootState} from "../../../../store/store";
import {
    setCenter,
    setSelectedCountry,
    setSelectedCountryBelongsToOtherRegion,
} from "../../../../features/map/mapSlice";
import {LATAM_COUNTRY_NAMES, ROMANIA_REGION,} from "../../../../features/countries/types";
import {isRegion} from "../../../../config";
import renderGeographyForLATAM from "./LATAM/renderGeographyForLATAM";
import renderGeographyForRomania from "./Romania/renderGeographyForRomania";

export const GeoMap: React.FC<GeoMapProps> = ({geographies}) => {
    const dispatch = useDispatch<AppDispatch>();
    const {
        map: {selectedCountry, caricomCountry, year},
        countriesPerUser: {data: countries},
    } = useSelector((state: RootState) => state);

    const handleCountrySelect = useCallback(
        (geography: any) => () => {
            const selectedCountry = countries.find(
                (country) => country.iso3 === geography.properties.iso3
            );
            const selectedCountryBelongsToCaricom = caricomCountry?.states.find(
                (state) => state.iso3 === geography.properties.iso3
            );

            if (selectedCountryBelongsToCaricom && caricomCountry) {
                dispatch(setCenter(caricomCountry.centro_id));
                dispatch(setSelectedCountry(caricomCountry));
                dispatch(
                    setSelectedCountryBelongsToOtherRegion(
                        selectedCountryBelongsToCaricom
                    )
                );
            }

            if (selectedCountry) {
                dispatch(setCenter(selectedCountry.centro_id));
                dispatch(setSelectedCountry(selectedCountry));
                dispatch(setSelectedCountryBelongsToOtherRegion(undefined));
            }
        },
        [caricomCountry, countries, dispatch]
    );

    const renderCaricom = useCallback(
        () =>
            geographies
                .filter((geo) =>
                    caricomCountry?.states.some(
                        (country) => country.iso3 === geo.properties.iso3
                    )
                )
                .map((geo) =>
                    renderGeographyForLATAM(
                        geo,
                        countries,
                        handleCountrySelect,
                        year,
                        selectedCountry,
                    )
                ),
        [
            caricomCountry,
            countries,
            geographies,
            handleCountrySelect,
            selectedCountry,
        ]
    );

    const renderSelectedCountryInLATAM = useCallback(() => {
        if (
            isRegion.latam &&
            selectedCountry?.country_name === LATAM_COUNTRY_NAMES.LATAM
        ) {
            return geographies.map((geo) =>
                renderGeographyForLATAM(
                    geo,
                    countries,
                    handleCountrySelect,
                    year,
                    selectedCountry,
                )
            );
        } else {
            return geographies
                .filter((geo) => geo.properties.iso3 === selectedCountry?.iso3)
                .map((geo) =>
                    renderGeographyForLATAM(
                        geo,
                        countries,
                        handleCountrySelect,
                        year,
                        selectedCountry,
                    )
                );
        }
    }, [
        countries,
        geographies,
        handleCountrySelect,
        selectedCountry,
    ]);

    const renderCountriesForLATAM =
        selectedCountry?.country_name === LATAM_COUNTRY_NAMES.CARICOM
            ? renderCaricom()
            : renderSelectedCountryInLATAM();

    const renderRomania = useCallback(() => {
        if (
            isRegion.romania &&
            selectedCountry?.country_name === ROMANIA_REGION.ROMANIA
        ) {
            return geographies.map((geo) =>
                renderGeographyForRomania(geo, handleCountrySelect, selectedCountry)
            );
        }
    }, [geographies, handleCountrySelect, selectedCountry]);

    const renderAllLATAM = useCallback(() => {
        geographies.map((geo) =>
            renderGeographyForLATAM(
                geo,
                countries,
                handleCountrySelect,
                year,
                selectedCountry,
            )
        );
    }, [
        caricomCountry,
        countries,
        geographies,
        handleCountrySelect,
        selectedCountry,
    ]);

    return (
        <>
            {isRegion.romania
                ? renderRomania()
                : isRegion.latam && selectedCountry
                    ? renderCountriesForLATAM
                    : renderAllLATAM()}
        </>
    );
};
